import React, {useEffect} from 'react';
import MonthInfo from "../month-info/month-info";
import TableCellCommon from "../table-cell/table-cell-common";
import css from "./date.module.css"
import {normalizeOccupationCode} from "../table-cell/occupation-codes";

const DateInfo = ({employees, refreshEmployees, mechanicSeparatorIndex}) => {
    const millisecondsInDay = 24 * 60 * 60 * 1000;
    const russianMonthNames = [
        'Январь',
        'Февраль',
        'Март',
        'Апрель',
        'Май',
        'Июнь',
        'Июль',
        'Август',
        'Сентябрь',
        'Октябрь',
        'Ноябрь',
        'Декабрь'
    ];
    const russianWeekdayNames = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

    function formatDateForSchedule(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}/${month}/${day}`;
    }

    function parseWorkDate(date) {
        if (date instanceof Date) {
            return Number.isNaN(date.getTime()) ? null : date;
        }

        if (date && typeof date === 'object') {
            return parseWorkDate(
                date.date || date.day || date.work_date || date.workDate || date.value
            );
        }

        if (typeof date === 'number') {
            const timestamp = Math.abs(date) < 100000000
                ? date * millisecondsInDay
                : date;

            const parsedDate = new Date(timestamp);
            return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
        }

        if (typeof date === 'string') {
            const trimmedDate = date.trim();
            const numericDate = Number(trimmedDate);

            if (trimmedDate !== '' && !Number.isNaN(numericDate)) {
                return parseWorkDate(numericDate);
            }

            const dateParts = trimmedDate.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);

            if (dateParts) {
                return new Date(Number(dateParts[1]), Number(dateParts[2]) - 1, Number(dateParts[3]));
            }

            const ruDateParts = trimmedDate.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);

            if (ruDateParts) {
                return new Date(Number(ruDateParts[3]), Number(ruDateParts[2]) - 1, Number(ruDateParts[1]));
            }

            const parsedDate = new Date(trimmedDate);
            return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
        }

        return null;
    }

    function normalizeWorkDate(date) {
        const normalizedDate = parseWorkDate(date);

        if (!normalizedDate) {
            return date;
        }

        return formatDateForSchedule(normalizedDate);
    }

    /**
     * Функция для получения всех месяцев из данных о работе сотрудников
     */
    function getAllMonths(employees) {
        return employees
            .flatMap(employee =>
                Object
                    .values(employee.work_days)
                    .flatMap(days =>
                        days.map(parseWorkDate)
                            .filter(Boolean)))
            .sort((a, b) => a - b);
    }

    /**
     * Форматирование месяца с годом на английском
     */
    function formatMonthYear(date) {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }

    /**
     * Форматирование месяца с годом на русском
     */
    function formatMonthYearInCell(date) {
        return `${russianMonthNames[date.getMonth()]} ${date.getFullYear()} г.`;
    }

    /**
     * Форматирование дня недели с числом месяца
     */
    function formatWeekDay(date) {
        return `${russianWeekdayNames[date.getDay()]},\n${String(date.getDate()).padStart(2, '0')}`;
    }

    /**
     * Функция для добавления недостающих месяцев
     */
    function addMonths(date, months) {
        const newDate = new Date(date);
        const day = newDate.getDate();

        newDate.setMonth(newDate.getMonth() + months);
        
        if (newDate.getDate() < day) {
            newDate.setDate(0);
        }

        return newDate;
    }

    /**
     * Функция для формирования массива всех месяцев с учетом недостающих
     */
    function getCompleteMonthArray(allMonths) {
        const monthsQuantity = [];

        let firstMonth, lastMonth;

        if (allMonths.length === 0) {
            const todayMonth = new Date();

            monthsQuantity.push(formatMonthYear(addMonths(todayMonth, -1)))
            monthsQuantity.push(formatMonthYear(todayMonth));

            lastMonth = addMonths(todayMonth, 0);
        } else {
            firstMonth = new Date(allMonths[0]);
            lastMonth = new Date(allMonths[allMonths.length - 1]);

            let currentDate = new Date(firstMonth);

            while (currentDate <= lastMonth) {
                monthsQuantity.push(formatMonthYear(currentDate));
                currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
            }

            firstMonth.setMonth(firstMonth.getMonth() - 1);
        }

        const lastMonthPlusOne = addMonths(lastMonth, 1);
        const lastMonthPlusTwo = addMonths(lastMonthPlusOne, 1);
        const lastMonthPlusThree = addMonths(lastMonthPlusTwo, 1);

        return [
            firstMonth && formatMonthYear(firstMonth),
            ...monthsQuantity,
            formatMonthYear(lastMonthPlusOne),
            formatMonthYear(lastMonthPlusTwo),
            formatMonthYear(lastMonthPlusThree)
        ].filter(Boolean);
    }

    /**
     * Функция для формирования объекта с информацией о месяцах и датах
     */
    function getMonthsInfo(updatedMonths) {
        return updatedMonths.map(monthYear => {
            const [year, month] = monthYear.split('-').map(Number);
            const daysCount = new Date(year, month, 0).getDate();
            const monthDates = [];

            for (let dayIndex = 1; dayIndex <= daysCount; dayIndex++) {
                monthDates.push(new Date(year, month - 1, dayIndex))
            }

            return {
                monthYear: monthYear,
                monthDates: monthDates,
            };
        });
    }

    /**
     * Основная функция для получения расписания
     */
    function getScheduleForMonths(monthsInfo, employees) {
        return monthsInfo.map(monthInfo => {
            return getSchedule(monthInfo.monthDates, employees);
        });
    }

    /**
     * Функция для получения расписания на конкретный месяц
     */
    function getSchedule(monthDates, employees) {
        let schedule = [];
        for (const index in employees) {
            const employeeData = employees[index];
            const normalizedWorkDays = {};

            for (const occupation in employeeData.work_days) {
                const occupationCode = normalizeOccupationCode(occupation);
                normalizedWorkDays[occupationCode] = new Set(
                    employeeData.work_days[occupation].map(normalizeWorkDate)
                );
            }

            const employeeWorkingDays = [];
            for (const date of monthDates) {
                const formattedDate = formatDateForSchedule(date);
                let dateIsWork = "emp";
                for (const occupation in normalizedWorkDays) {
                    if (normalizedWorkDays[occupation].has(formattedDate)) {
                        if (dateIsWork === "emp") {
                            dateIsWork = occupation;
                        }
                    }
                }
                let dateWorkInfo = {"date": formattedDate, "dateIsWork": dateIsWork}
                employeeWorkingDays.push(dateWorkInfo);
            }
            const employeeSchedule = {
                article: employeeData.url,
                workDaysData: employeeWorkingDays
            }
            schedule.push(employeeSchedule);
        }
        return schedule;
    }

    const allMonths = getAllMonths(employees);
    const monthsQuantity = getCompleteMonthArray(allMonths);
    const monthsInfo = getMonthsInfo(monthsQuantity);
    const scheduleForMonths = getScheduleForMonths(monthsInfo, employees);

    /**
     * Фокусирует на сегодняшнем дне
     */
    useEffect(() => {
        const today = new Date();
        const formattedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        const element = document.querySelector(`[dateTime="${formattedToday}"]`);

        if (element) {
            element.setAttribute('tabIndex', '-1');
            element.focus();
        }
    }, []);

    return (
        <div style={{display: 'flex'}}>
            {monthsInfo.map((monthInfo, index) =>
                <div className={"month_block"}
                     style={{width: `calc(var(--schedule-day-width) * ${monthInfo.monthDates.length})`}}
                     key={index}>
                    <div className={css.table__month__column__header}>
                        <div className={css.year_title}>
                            <TableCellCommon
                                key={index}
                                url={monthInfo.monthYear}
                                item={
                                    (() => {
                                        const [year, month] = monthInfo.monthYear.split('-').map(Number);
                                        const formattedDate = new Date(year, month - 1, 1);
                                        const formattedDateInCell = formatMonthYearInCell(formattedDate);

                                        return formattedDateInCell.charAt(0).toUpperCase() +
                                            formattedDateInCell.slice(1);
                                    })()
                                }
                            />
                        </div>
                        <div
                            className={css.week_title}
                            style={{gridTemplateColumns: `repeat(${monthInfo.monthDates.length}, var(--schedule-day-width))`}}
                        >
                            {monthInfo.monthDates.map((date, dateIndex) =>
                                <TableCellCommon
                                    classes={css.table__weekday_cell}
                                    key={dateIndex}
                                    url={index}
                                    item={
                                        formatWeekDay(date).charAt(0).toUpperCase() +
                                        formatWeekDay(date).slice(1)
                                    }
                                    dateTime={date}
                                />
                            )}
                        </div>
                    </div>
                    <MonthInfo
                        key={index}
                        month={monthInfo.monthYear}
                        dates={scheduleForMonths[index]}
                        daysCount={monthInfo.monthDates.length}
                        refreshEmployees={refreshEmployees}
                        mechanicSeparatorIndex={mechanicSeparatorIndex}
                    />
                </div>
            )}
        </div>
    );
};

export default DateInfo;
